import { morphSection } from '@theme/section-renderer';
import { startViewTransition } from '@theme/utilities';

const MOBILE_QUERY = '(max-width: 749px)';
const AVAILABILITY_PARAM = 'filter.v.availability';

/**
 * Several values of one metafield filter are sent as one comma-separated parameter, which Shopify treats as "any of
 * them" (OR): ?filter.p.m.custom.card_color=Red,Blue. Shopify only understands the comma when it is written as a plain
 * comma, so the address has to keep it unencoded (URLSearchParams would turn it into %2C, which matches nothing).
 * Different filters are still combined with AND.
 */

/**
 * The values chosen for one metafield filter in an address.
 * @param {URL} url
 * @param {string} param
 * @returns {string[]}
 */
function getFilterValues(url, param) {
  return url.searchParams
    .getAll(param)
    .flatMap((value) => value.split(','))
    .filter((value) => value !== '');
}

/**
 * Writes the chosen values of one metafield filter into an address (or removes the filter when none are chosen).
 * @param {URL} url
 * @param {string} param
 * @param {string[]} values
 */
function setFilterValues(url, param, values) {
  url.searchParams.delete(param);
  if (values.length > 0) url.searchParams.set(param, values.join(','));
}

/**
 * Puts the commas back as plain commas after URLSearchParams has encoded them.
 * @param {URL} url
 * @returns {URL}
 */
function withRawCommas(url) {
  url.search = url.search.replace(/%2C/gi, ',');
  return url;
}

/** @type {Map<string, string>} */
const sectionCache = new Map();

/**
 * Loads one section's HTML for an address (the Section Rendering API). The theme's own section renderer re-encodes the
 * commas, so this builds the request itself and keeps them plain.
 * @param {string} sectionId
 * @param {URL} url
 * @param {{ signal?: AbortSignal, cache?: boolean }} [options]
 * @returns {Promise<string>}
 */
async function fetchSectionHTML(sectionId, url, { signal, cache = false } = {}) {
  const id = sectionId.replace(/^shopify-section-/, '');
  const request = new URL(url);
  request.searchParams.delete('section_id');

  // Preview address parameters can change how a filter with several values is read, and the preview cookie already
  // picks the theme, so leave the parameter out of the product request (but not in the theme editor).
  if (!window.Shopify?.designMode && request.search.includes(',')) request.searchParams.delete('preview_theme_id');

  const search = request.search.replace(/%2C/gi, ',');
  const requestUrl = `${request.pathname}${search}${search ? '&' : '?'}section_id=${encodeURIComponent(id)}`;

  if (cache && sectionCache.has(requestUrl)) return /** @type {string} */ (sectionCache.get(requestUrl));

  const response = await fetch(requestUrl, { signal });
  const html = await response.text();
  if (response.ok) sectionCache.set(requestUrl, html);
  return html;
}

/**
 * Behavior for the advanced card filter section, the one menu for a collection page.
 *
 * Card filter options are normal links to Shopify tag URLs (/collections/handle/color_red+rarity_rare), so the filter
 * works without JavaScript. With JavaScript the address is updated and the product grid is re-rendered in place,
 * the same way the theme's own filters do, instead of reloading the page.
 *
 * Desktop: every choice applies straight away.
 * Mobile: choices made in the drawer are only staged (the tick shows instantly, nothing loads) and are applied
 * together when the shopper taps "Show results".
 *
 * The menu itself is never re-fetched: the address is the source of truth, and the menu is updated locally.
 * The products are fetched first (the page stays responsive and shows a loading line), and only the quick swap of
 * the new products into the page happens inside the view transition.
 */
class AdvancedCardFilter extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** The request for the products that is currently loading, so a newer choice can cancel it. */
  /** @type {AbortController | null} */
  #pending = null;

  /** @type {HTMLElement | null} */
  #lastFocus = null;

  #scrollLockValue = '';

  /** What the drawer looked like when it opened, so closing without applying can put it back. */
  /** @type {{ active: HTMLElement[], prices: string[], sorts: string[] } | null} */
  #snapshot = null;

  /** @type {Set<string>} */
  #prefetched = new Set();

  #mobile = window.matchMedia(MOBILE_QUERY);

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('input', this.#handleInput, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.addEventListener('toggle', this.#handleToggle, { signal, capture: true });
    this.addEventListener('pointerover', this.#handlePointerOver, { signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal });

    document.addEventListener('click', this.#handleOutsideClick, { signal });
    window.addEventListener('popstate', this.#handlePopState, { signal });
    window.addEventListener('load', () => this.#syncDensityFromGrid(), { signal, once: true });
    this.#mobile.addEventListener('change', this.#handleBreakpointChange, { signal });

    // The address is the truth (the server may not offer an availability filter at all on some collections)
    this.#applyStateFromUrl();
    this.#syncDensityFromGrid();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#pending?.abort();
    this.#unlockScroll();
  }

  get #drawer() {
    return this.querySelector('[data-acf-drawer]');
  }

  get #toggleButton() {
    return this.querySelector('[data-acf-open]');
  }

  get #isOpen() {
    return this.classList.contains('is-open');
  }

  get #collectionPath() {
    return this.dataset.collectionUrl ?? '';
  }

  /** @returns {HTMLElement[]} */
  get #options() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-acf-option]')]);
  }

  /** @returns {HTMLInputElement[]} */
  get #priceInputs() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('[data-acf-price]')]);
  }

  /** @returns {HTMLSelectElement[]} */
  get #sortSelects() {
    return /** @type {HTMLSelectElement[]} */ ([...this.querySelectorAll('[data-acf-sort]')]);
  }

  /**
   * The id of the section that renders the product grid, so it can be re-rendered when the filters change.
   * @returns {string | null}
   */
  get #productSectionId() {
    const grid = document.querySelector('.product-grid-container[section-id]');
    return (
      grid?.getAttribute('section-id') ??
      document.querySelector('facets-form-component[section-id]')?.getAttribute('section-id') ??
      null
    );
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-acf-open]')) {
      this.#openDrawer();
      return;
    }

    if (target.closest('[data-acf-apply]')) {
      this.#applyStaged();
      return;
    }

    if (target.closest('[data-acf-close]')) {
      this.#closeDrawer({ discard: true });
      return;
    }

    if (target.closest('[data-acf-instock]')) {
      this.#toggleInstock();
      return;
    }

    if (target.closest('[data-acf-apply-price]')) {
      this.#navigate(this.#buildUrl());
      return;
    }

    const isPlainClick = !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0);

    if (target.closest('[data-acf-clear-staged]')) {
      if (!isPlainClick) return;
      event.preventDefault();
      this.#clearStaged();
      return;
    }

    if (target.closest('[data-acf-clear]')) {
      if (!isPlainClick) return;
      event.preventDefault();
      this.#clearStaged();
      this.#navigate(this.#buildUrl({ ignoreExtraTags: true }));
      return;
    }

    const chip = target.closest('[data-acf-chip]');
    if (chip instanceof HTMLElement) {
      if (!isPlainClick) return;
      event.preventDefault();
      const option = this.#options.find((candidate) => candidate.dataset.handle === chip.dataset.handle);
      if (option) this.#setActive(option, false);
      this.#updateBadges();
      this.#navigate(this.#buildUrl());
      return;
    }

    const option = target.closest('[data-acf-option]');
    if (option instanceof HTMLElement) {
      // Let the browser handle new-tab clicks and the like
      if (!isPlainClick) return;
      event.preventDefault();

      this.#toggleOption(option);
      this.#updateBadges();

      // Mobile: only stage the choice. Desktop: apply it now.
      if (this.#mobile.matches) return;

      // On desktop, single-choice groups close their dropdown once a value is picked
      if (option.dataset.multi !== 'true') option.closest('details')?.removeAttribute('open');
      this.#navigate(this.#buildUrl());
    }
  };

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    const target = event.target;

    if (target instanceof HTMLSelectElement && target.hasAttribute('data-acf-sort')) {
      // Keep the desktop and mobile sort menus the same
      for (const select of this.#sortSelects) select.value = target.value;
      if (!this.#mobile.matches) this.#navigate(this.#buildUrl());
      return;
    }

    if (target instanceof HTMLInputElement && target.classList.contains('column-options__option-input')) {
      const list = /** @type {(HTMLElement & { updateLayout?: (event: { target: HTMLInputElement }) => void }) | null} */ (
        document.querySelector('results-list')
      );
      list?.updateLayout?.({ target });
    }
  };

  /**
   * Live search inside a group's values, and the price badge.
   * @param {Event} event
   */
  #handleInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (input.hasAttribute('data-acf-price')) {
      this.#updateBadges();
      return;
    }

    if (!input.hasAttribute('data-acf-search')) return;

    const query = input.value.trim().toLowerCase();
    const panel = input.closest('.acf__panel');

    for (const item of panel?.querySelectorAll('.acf__item') ?? []) {
      const label = item.querySelector('.acf__option-label')?.textContent?.toLowerCase() ?? '';
      /** @type {HTMLElement} */ (item).hidden = query !== '' && !label.includes(query);
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (this.#isOpen) {
        event.stopPropagation();
        this.#closeDrawer({ discard: true });
        return;
      }

      const open = this.querySelector('details[open]');
      if (open) {
        open.removeAttribute('open');
        open.querySelector('summary')?.focus();
      }
      return;
    }

    if (event.key === 'Enter' && event.target instanceof HTMLInputElement && event.target.hasAttribute('data-acf-price')) {
      event.preventDefault();
      if (!this.#mobile.matches) this.#navigate(this.#buildUrl());
      return;
    }

    if (event.key === 'Tab' && this.#isOpen) this.#trapFocus(event);
  };

  /**
   * On desktop only one dropdown is open at a time, and dropdowns near the right edge open leftwards.
   * @param {Event} event
   */
  #handleToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open || this.#mobile.matches) return;

    for (const other of this.querySelectorAll('details[open]')) {
      if (other !== details) other.removeAttribute('open');
    }

    const panel = details.querySelector('.acf__panel');
    details.classList.remove('acf__group--align-end');
    if (panel && panel.getBoundingClientRect().right > window.innerWidth - 8) {
      details.classList.add('acf__group--align-end');
    }
  };

  /**
   * Close the dropdowns when clicking anywhere else on desktop.
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    if (this.#mobile.matches) return;
    const target = event.target;
    if (!(target instanceof Node)) return;

    for (const details of this.querySelectorAll('details[open]')) {
      if (!details.contains(target)) details.removeAttribute('open');
    }
  };

  /**
   * Warm up the products for the in stock switch as soon as the pointer is on it, so the click feels instant.
   * @param {Event} event
   */
  #handlePointerOver = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-acf-instock]')) this.#prefetchInstock();
  };

  /**
   * @param {Event} event
   */
  #handleFocusIn = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-acf-instock]')) this.#prefetchInstock();
  };

  #handleBreakpointChange = () => {
    if (this.#isOpen) this.#closeDrawer({ discard: true, restoreFocus: false });
  };

  #handlePopState = () => {
    this.#applyStateFromUrl();
    this.#refresh().catch(() => window.location.reload());
  };

  /* ------------------------------------------------------------------ choices */

  /**
   * @param {HTMLElement} option
   */
  #toggleOption(option) {
    if (option.classList.contains('is-active')) {
      this.#setActive(option, false);
      return;
    }

    // Single-choice groups only hold one value at a time
    if (option.dataset.multi !== 'true') {
      for (const other of this.#options) {
        if (other !== option && other.dataset.group === option.dataset.group) this.#setActive(other, false);
      }
    }

    this.#setActive(option, true);
  }

  /**
   * @param {HTMLElement} option
   * @param {boolean} active
   */
  #setActive(option, active) {
    option.classList.toggle('is-active', active);
    if (active) option.setAttribute('aria-current', 'true');
    else option.removeAttribute('aria-current');
  }

  /**
   * Tags in the address that none of the filter groups know about, so they are not lost.
   * @returns {string[]}
   */
  #extraPathTags() {
    const known = new Set(this.#options.map((option) => option.dataset.handle));
    return this.#pathTags().filter((handle) => !known.has(handle));
  }

  /**
   * All the tags in the current address.
   * @returns {string[]}
   */
  #pathTags() {
    const base = this.#collectionPath;
    const path = decodeURIComponent(window.location.pathname);
    if (!base || !path.startsWith(`${base}/`)) return [];

    return (path.slice(base.length + 1).split('/')[0] ?? '').split('+').filter((handle) => handle !== '');
  }

  /**
   * The address for whatever is currently chosen in the menu.
   * @param {{ ignoreExtraTags?: boolean }} [options]
   * @returns {URL}
   */
  #buildUrl({ ignoreExtraTags = false } = {}) {
    // Tag groups go in the path (/collections/handle/color_red+cost_5). Metafield groups go in the query (below).
    const tags = [
      ...this.#options
        .filter((option) => !option.dataset.param && option.classList.contains('is-active'))
        .map((option) => option.dataset.handle),
      ...(ignoreExtraTags ? [] : this.#extraPathTags()),
    ].filter(Boolean);

    const path = tags.length > 0 ? `${this.#collectionPath}/${tags.join('+')}` : this.#collectionPath;
    const url = new URL(path, window.location.origin);

    // Keep everything else in the address, like in stock, then apply what the menu says
    url.search = window.location.search;

    // Metafield groups: ?filter.p.m.namespace.key=value1,value2 (one parameter per group). Cards with ANY of them match.
    const metafieldParams = new Set(this.#options.map((option) => option.dataset.param).filter(Boolean));
    for (const param of metafieldParams) {
      const values = this.#options
        .filter((option) => option.dataset.param === param && option.classList.contains('is-active'))
        .map((option) => option.dataset.value ?? '');
      setFilterValues(url, param, values);
    }

    for (const input of this.#priceInputs) {
      const value = input.value.replace(/[^0-9.]/g, '');
      if (value !== '' && !Number.isNaN(Number(value))) url.searchParams.set(input.name, value);
      else url.searchParams.delete(input.name);
    }

    const sort = this.#sortSelects[0];
    if (sort) {
      if (sort.value === sort.dataset.default) url.searchParams.delete('sort_by');
      else url.searchParams.set('sort_by', sort.value);
    }

    url.searchParams.delete('page');
    return withRawCommas(url);
  }

  /**
   * @param {boolean} on
   * @returns {URL}
   */
  #instockUrl(on) {
    const url = this.#buildUrl();
    if (on) url.searchParams.set(AVAILABILITY_PARAM, '1');
    else url.searchParams.delete(AVAILABILITY_PARAM);
    return withRawCommas(url);
  }

  #toggleInstock() {
    const button = this.querySelector('[data-acf-instock]');
    if (!(button instanceof HTMLElement)) return;

    const on = button.getAttribute('aria-checked') !== 'true';
    button.setAttribute('aria-checked', String(on));

    const url = this.#instockUrl(on);

    // The whole collection has nothing out of stock, so "in stock" can't change the products: just update the address
    if (button.dataset.oosNone === 'true') {
      history.pushState('', '', url.toString());
      this.#renderChips();
      return;
    }

    this.#navigate(url);
  }

  /**
   * Loads the products for the switch's other position in the background.
   */
  #prefetchInstock() {
    const button = this.querySelector('[data-acf-instock]');
    const productSectionId = this.#productSectionId;
    if (!(button instanceof HTMLElement) || !productSectionId || button.dataset.oosNone === 'true') return;

    const url = this.#instockUrl(button.getAttribute('aria-checked') !== 'true');
    const key = url.toString();
    if (this.#prefetched.has(key) || window.Shopify?.designMode) return;

    this.#prefetched.add(key);
    fetchSectionHTML(productSectionId, url, { cache: true }).catch(() => this.#prefetched.delete(key));
  }

  #clearStaged() {
    for (const option of this.#options) this.#setActive(option, false);
    for (const input of this.#priceInputs) input.value = '';
    this.#updateBadges();
  }

  /**
   * Counts on each group, on the Filters button, and the visibility of the clear button.
   */
  #updateBadges() {
    let total = 0;

    for (const badge of this.querySelectorAll('[data-acf-count]')) {
      const group = badge.getAttribute('data-group');
      const count =
        group === 'price'
          ? Number(this.#priceInputs.some((input) => input.value.trim() !== ''))
          : this.querySelectorAll(`[data-acf-option][data-group="${group}"].is-active`).length;

      badge.textContent = String(count);
      /** @type {HTMLElement} */ (badge).hidden = count === 0;
      total += count;
    }

    const totalBadge = this.querySelector('[data-acf-total]');
    if (totalBadge) {
      totalBadge.textContent = String(total);
      /** @type {HTMLElement} */ (totalBadge).hidden = total === 0;
    }

    const clear = this.querySelector('[data-acf-clear-staged]');
    if (clear) /** @type {HTMLElement} */ (clear).hidden = total === 0;
  }

  /**
   * Makes the whole menu match the address: ticks, in stock, price and sort.
   * Used when the page loads and when the shopper goes back or forward.
   */
  #applyStateFromUrl() {
    const url = new URL(window.location.href);
    const tags = new Set(this.#pathTags());

    for (const option of this.#options) {
      const active = option.dataset.param
        ? getFilterValues(url, option.dataset.param).includes(option.dataset.value ?? '')
        : tags.has(option.dataset.handle ?? '');
      this.#setActive(option, active);
    }

    this.querySelector('[data-acf-instock]')?.setAttribute(
      'aria-checked',
      String(url.searchParams.get(AVAILABILITY_PARAM) === '1')
    );

    for (const input of this.#priceInputs) input.value = url.searchParams.get(input.name) ?? '';

    for (const select of this.#sortSelects) {
      const wanted = url.searchParams.get('sort_by') ?? select.dataset.default ?? '';
      if ([...select.options].some((option) => option.value === wanted)) select.value = wanted;
    }

    this.#updateBadges();
    this.#updateHrefs();
    this.#renderChips();
  }

  /**
   * Keeps every option's link right for the current choices, so opening one in a new tab still works.
   */
  #updateHrefs() {
    const options = this.#options;
    const base = this.#collectionPath;
    const extras = this.#extraPathTags();
    const current = this.#buildUrl();

    // Tag options only: metafield options live in the query, not the path
    const tagOptions = options.filter((option) => !option.dataset.param);
    const active = tagOptions.filter((option) => option.classList.contains('is-active')).map((option) => option.dataset.handle ?? '');

    /** @type {Map<string, Set<string>>} */
    const handlesByGroup = new Map();
    for (const option of tagOptions) {
      const group = option.dataset.group ?? '';
      if (!handlesByGroup.has(group)) handlesByGroup.set(group, new Set());
      handlesByGroup.get(group)?.add(option.dataset.handle ?? '');
    }

    for (const option of options) {
      const url = new URL(current);
      const isActive = option.classList.contains('is-active');

      if (option.dataset.param) {
        const param = option.dataset.param;
        const value = option.dataset.value ?? '';
        const values = getFilterValues(url, param).filter((candidate) => candidate !== value);
        if (!isActive) values.push(value);

        setFilterValues(url, param, values);
      } else {
        const handle = option.dataset.handle ?? '';
        let next;

        if (isActive) {
          next = active.filter((candidate) => candidate !== handle);
        } else {
          const sameGroup = handlesByGroup.get(option.dataset.group ?? '');
          next = option.dataset.multi === 'true' ? [...active] : active.filter((candidate) => !sameGroup?.has(candidate));
          next.push(handle);
        }

        const all = [...next, ...extras];
        url.pathname = all.length > 0 ? `${base}/${all.join('+')}` : base;
      }

      withRawCommas(url);
      option.setAttribute('href', `${url.pathname}${url.search}`);
    }
  }

  /**
   * The selected filters shown above the products, built from what is ticked in the menu.
   */
  #renderChips() {
    const container = this.querySelector('[data-acf-chips]');
    if (!(container instanceof HTMLElement)) return;

    const clear = container.querySelector('[data-acf-clear]');
    for (const chip of container.querySelectorAll('[data-acf-chip]')) chip.remove();

    const active = this.#options.filter((option) => option.classList.contains('is-active'));

    for (const option of active) {
      const group = option.closest('details')?.querySelector('.acf__summary-label')?.textContent?.trim() ?? '';
      const value = option.querySelector('.acf__option-label')?.textContent?.trim() ?? '';

      const chip = document.createElement('a');
      chip.className = 'acf__chip';
      chip.href = option.getAttribute('href') ?? '#';
      chip.rel = 'nofollow';
      chip.setAttribute('data-acf-chip', '');
      chip.dataset.handle = option.dataset.handle ?? '';
      chip.setAttribute('aria-label', `Remove filter ${group}: ${value}`);

      const text = document.createElement('span');
      text.textContent = `${group}: ${value}`;

      const remove = document.createElement('span');
      remove.className = 'acf__chip-x';
      remove.setAttribute('aria-hidden', 'true');
      remove.textContent = '×';

      chip.append(text, remove);
      container.insertBefore(chip, clear);
    }

    container.hidden = active.length === 0;
  }

  /* ------------------------------------------------------------------- drawer */

  #openDrawer() {
    const drawer = this.#drawer;
    if (!drawer) return;

    this.#snapshot = {
      active: this.#options.filter((option) => option.classList.contains('is-active')),
      prices: this.#priceInputs.map((input) => input.value),
      sorts: this.#sortSelects.map((select) => select.value),
    };

    this.#lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.classList.add('is-open');
    this.#toggleButton?.setAttribute('aria-expanded', 'true');
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');

    // Show what is already selected
    for (const group of this.querySelectorAll('details')) {
      if (group.querySelector('.acf__option.is-active') || group.querySelector('[data-acf-count]:not([hidden])')) {
        group.setAttribute('open', '');
      }
    }

    this.#lockScroll();
    /** @type {HTMLElement | null} */ (this.querySelector('.acf__close'))?.focus();
  }

  /**
   * @param {{ discard?: boolean, restoreFocus?: boolean }} [options]
   */
  #closeDrawer({ discard = false, restoreFocus = true } = {}) {
    if (discard && this.#snapshot) this.#restoreSnapshot(this.#snapshot);
    this.#snapshot = null;

    this.classList.remove('is-open');
    this.#toggleButton?.setAttribute('aria-expanded', 'false');
    this.#drawer?.removeAttribute('role');
    this.#drawer?.removeAttribute('aria-modal');
    this.#unlockScroll();

    if (restoreFocus) this.#lastFocus?.focus();
    this.#lastFocus = null;
  }

  /**
   * Puts the drawer back the way it was when it opened (choices that were never applied are dropped).
   * @param {{ active: HTMLElement[], prices: string[], sorts: string[] }} snapshot
   */
  #restoreSnapshot(snapshot) {
    for (const option of this.#options) this.#setActive(option, snapshot.active.includes(option));
    this.#priceInputs.forEach((input, index) => {
      input.value = snapshot.prices[index] ?? '';
    });
    this.#sortSelects.forEach((select, index) => {
      select.value = snapshot.sorts[index] ?? select.value;
    });
    this.#updateBadges();
  }

  /**
   * "Show results": apply everything chosen in the drawer in one go.
   */
  #applyStaged() {
    const url = this.#buildUrl();
    this.#closeDrawer({ discard: false });
    this.#navigate(url);
  }

  #lockScroll() {
    this.#scrollLockValue = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  #unlockScroll() {
    if (document.body.style.overflow === 'hidden') document.body.style.overflow = this.#scrollLockValue;
  }

  /**
   * Keeps keyboard focus inside the drawer while it is open.
   * @param {KeyboardEvent} event
   */
  #trapFocus(event) {
    const drawer = this.#drawer;
    if (!drawer) return;

    const focusable = /** @type {HTMLElement[]} */ ([
      ...drawer.querySelectorAll('a[href], button:not([disabled]), input, select, summary'),
    ]).filter((element) => element.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  /* --------------------------------------------------------------- navigation */

  /**
   * Go to a new address without reloading: update the address, load the products, update the menu.
   * @param {URL} target
   */
  #navigate(target) {
    if (target.toString() === window.location.href) return;

    // Nothing to re-render on a page without a product grid: just go there
    if (!this.#productSectionId) {
      window.location.assign(target.toString());
      return;
    }

    history.pushState('', '', target.toString());
    this.#updateHrefs();
    this.#renderChips();
    this.#refresh().catch(() => window.location.assign(target.toString()));
  }

  /**
   * Loads and swaps in the products for the current address.
   *
   * The products are fetched first, while the page stays fully responsive (the switch, the loading line). Only the swap
   * itself runs inside the view transition, so the page is never frozen while waiting for the server.
   * @returns {Promise<void>}
   */
  async #refresh() {
    const productSectionId = this.#productSectionId;
    if (!productSectionId) {
      window.location.reload();
      return;
    }

    this.#pending?.abort();
    const controller = new AbortController();
    this.#pending = controller;

    const grid = document.querySelector('.product-grid-container');
    this.classList.add('is-loading');
    grid?.classList.add('acf-grid-loading');
    this.querySelector('[data-acf-results]')?.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(window.location.href);
      const html = await fetchSectionHTML(productSectionId, url, {
        signal: controller.signal,
        cache: !window.Shopify?.designMode,
      });

      // The shopper made another choice while this one was loading
      if (controller.signal.aborted) return;

      await startViewTransition(() => morphSection(productSectionId, html), ['product-grid']);

      if (!this.#updateCountFromGrid()) await this.#updateCountFromServer(url);
      this.#syncDensityFromGrid();
    } catch (error) {
      if (controller.signal.aborted) return;
      throw error;
    } finally {
      if (this.#pending === controller) {
        this.#pending = null;
        this.classList.remove('is-loading');
        grid?.classList.remove('acf-grid-loading');
        document.querySelector('.product-grid-container')?.classList.remove('acf-grid-loading');
        this.querySelector('[data-acf-results]')?.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * The item count, read from the theme's own count (which is still on the page, just hidden).
   * @returns {boolean} Whether a count was found
   */
  #updateCountFromGrid() {
    const text = document.querySelector('.products-count-wrapper [role="status"]')?.textContent?.trim();
    const target = this.querySelector('[data-acf-results]');
    if (!text || !target) return false;

    target.textContent = text;
    return true;
  }

  /**
   * Only used when the theme's own count is not on the page (its bar was turned off in the editor).
   * @param {URL} url
   */
  async #updateCountFromServer(url) {
    const sectionId = this.dataset.sectionId;
    const target = this.querySelector('[data-acf-results]');
    if (!sectionId || !target) return;

    const html = await fetchSectionHTML(sectionId, new URL(url));
    const text = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-acf-results]')?.textContent;
    if (text) target.textContent = text;
  }

  /**
   * The grid layout buttons live here now, so show the layout the product grid is actually using.
   */
  #syncDensityFromGrid() {
    const view = document.querySelector('results-list [ref="grid"]')?.getAttribute('product-grid-view');
    if (!view) return;

    for (const input of this.querySelectorAll('.column-options__option-input')) {
      if (input instanceof HTMLInputElement && input.value === view) input.checked = true;
    }
  }
}

if (!customElements.get('advanced-card-filter')) {
  customElements.define('advanced-card-filter', AdvancedCardFilter);
}
